/**
 * layout - the contact page's binding of the shared detail-page surface.
 *
 * A contact has four panes over its canvas:
 *
 *   context   type, status, onboarding, email/phone/address, timestamps
 *             (left, open)
 *   access    who is on this contact, and the lead              (right)
 *   activity  the audit log                                    (right)
 *   reader    a document, read without leaving the page        (right, no rail)
 *
 * The centre is the CANVAS - the tab bar and whichever tab is open (Details,
 * Matters, Documents, Billing) - always present, never tiled, with the panes
 * floating over it.
 *
 * WHAT THESE REPLACE. The facts were four `FactTile` grids halfway down the
 * Details tab, so "what type of client is this, are they still active, what is
 * their email" left the screen the moment someone opened Matters or Documents
 * - which is exactly when those get asked. The audit log was a hand-rolled
 * `fixed end-0 top-0 h-full w-96 glass` drawer with its own scrim, living
 * outside every layout the page had, reachable only from a menu item that set
 * an `auditTarget` object. Opening a document was an `attachmentViewer` modal
 * over the whole page, so you could read the file or see the list, never both.
 *
 * WHY ENTITY SCOPE - and this is where a contact differs from a task. A task
 * is one of four hundred rows opened for a minute, so tasks share one
 * arrangement. A contact is a record someone comes back to for years: the
 * corporate client worked from its matters and the individual worked from its
 * documents want different panes open, and one shared key would make every
 * contact inherit the arrangement of whichever was opened last. So the
 * contact's id is part of the storage key.
 *
 * NO LEGACY KEYS. This surface is new; nothing was ever written for it.
 *
 * NO DETAIL PANE. The page edits the contact inline on the Details tab and
 * selects no row into a shared editor, so it binds the selection-free hook —
 * see `./index`.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';

export type ContactPanelId = 'context' | 'access' | 'activity' | 'reader';

/** The canvas's tabs. Labels are the page's - see `ClientDetails`. */
export type ContactTab = 'Details' | 'Matters' | 'Documents' | 'Billing';

export const CONTACT_TABS: ContactTab[] = ['Details', 'Matters', 'Documents', 'Billing'];

export type ContactPanelSlot = GenericSlot<ContactPanelId>;
export type ContactPanelLayout = GenericLayout<ContactPanelId>;

export const CONTACT_PANEL_IDS: ContactPanelId[] = [
  'context',
  'access',
  'activity',
  'reader',
];

/**
 * Every pane: its label, its resize limits, where it starts, whether it opens
 * on a first visit and whether it gets a rail toggle.
 *
 * ONE IS OPEN BY DEFAULT - context - because those facts were on screen
 * before, as the Details tab's `FactTile` grids. Opening on defaults nobody
 * chose is how a returning user loses their bearings, so the pane set starts
 * where the fixed layout ended.
 */
const CONTACT_PANES: Record<ContactPanelId, DetailPaneConfig> = {
  // The facts of the contact, read against whatever tab is open - which is the
  // whole reason they moved off the Details tab.
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  /*
    Access was a `GlassCard` halfway down the Details tab - the only page in
    the app that read its roster from the canvas rather than a pane, so it
    left the screen the moment someone opened Matters or Documents, and it
    carried its own row anatomy and its own access-level mapping. It is the
    shared pane now.
  */
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 0, width: 300, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
  /**
   * NO RAIL PILL. The reader opens because someone pressed a document in the
   * Documents tab; a toggle for it would be a control that does nothing until
   * you use the page, and would open onto "nothing selected".
   */
  reader: {
    label: 'Reader',
    bounds: { min: 320, max: 900 },
    dock: 'right', index: 2, width: 560, visible: false,
  },
};

export const CONTACT_SURFACE = defineDetailSurface<ContactPanelId, ContactTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `contact-surface`, etc.
  name: 'contact',
  scope: 'entity',
  ids: CONTACT_PANEL_IDS,
  panes: CONTACT_PANES,
  tabs: CONTACT_TABS,
  readerPane: 'reader',
});

/* ── derived, for call sites and tests that want the plain records ── */

export const CONTACT_PANEL_LABELS: Record<ContactPanelId, string> = CONTACT_SURFACE.labels;
export const CONTACT_PANEL_BOUNDS: Record<ContactPanelId, PanelBounds> = CONTACT_SURFACE.bounds;
export const CONTACT_DEFAULT_LAYOUT: ContactPanelLayout = CONTACT_SURFACE.defaults;
